<script setup lang="ts">
import type {
  VideoCallInputDevice,
  VideoCallInputDeviceKind,
  VideoCallMediaDevicesStatus,
} from "~/types/videoCalls";

const props = withDefaults(
  defineProps<{
    cameraDevices?: VideoCallInputDevice[];
    microphoneDevices?: VideoCallInputDevice[];
    selectedCameraDeviceId?: string;
    selectedMicrophoneDeviceId?: string;
    mediaReady?: boolean;
    mediaDevicesStatus?: VideoCallMediaDevicesStatus;
    mediaDevicesError?: string;
    switchingDeviceKind?: VideoCallInputDeviceKind | null;
    overlay?: boolean;
  }>(),
  {
    cameraDevices: () => [],
    microphoneDevices: () => [],
    selectedCameraDeviceId: "",
    selectedMicrophoneDeviceId: "",
    mediaReady: false,
    mediaDevicesStatus: "idle",
    mediaDevicesError: "",
    switchingDeviceKind: null,
    overlay: false,
  },
);

const emit = defineEmits<{
  opened: [];
  "select-input-device": [kind: VideoCallInputDeviceKind, deviceId: string];
}>();

const menuOpen = ref(false);
const activatorElement = ref<HTMLButtonElement | null>(null);
const closeElement = ref<HTMLButtonElement | null>(null);

const deviceSelectionDisabled = computed(
  () =>
    !props.mediaReady ||
    props.mediaDevicesStatus === "loading" ||
    props.mediaDevicesStatus === "unsupported" ||
    Boolean(props.switchingDeviceKind),
);

const deviceStatusMessage = computed(() => {
  if (!props.mediaReady) {
    return "Configure a câmera e o microfone primeiro para escolher os dispositivos.";
  }
  if (props.switchingDeviceKind === "videoinput") {
    return "Trocando câmera…";
  }
  if (props.switchingDeviceKind === "audioinput") {
    return "Trocando microfone…";
  }
  if (props.mediaDevicesStatus === "loading") {
    return "Carregando dispositivos…";
  }
  if (props.mediaDevicesStatus === "unsupported") {
    return "Este navegador não permite escolher câmera e microfone.";
  }
  if (props.mediaDevicesStatus === "error") {
    return (
      props.mediaDevicesError ||
      "Não foi possível carregar os dispositivos disponíveis."
    );
  }
  return "";
});

const getDeviceLabel = (
  device: VideoCallInputDevice,
  index: number,
  fallback: string,
) => device.label || `${fallback} ${index + 1}`;

const selectDevice = (kind: VideoCallInputDeviceKind, event: Event) => {
  const deviceId = (event.target as HTMLSelectElement).value;
  if (!deviceId) return;
  emit("select-input-device", kind, deviceId);
};

const focusCloseElement = () => {
  if (menuOpen.value) closeElement.value?.focus({ preventScroll: true });
};

const restoreActivatorFocus = () => {
  if (!menuOpen.value) {
    activatorElement.value?.focus({ preventScroll: true });
  }
};

watch(menuOpen, (isOpen) => {
  if (isOpen) {
    emit("opened");
  }
});
</script>

<template>
  <v-menu
    v-model="menuOpen"
    :close-on-content-click="false"
    :min-width="0"
    :max-width="336"
    :z-index="2500"
    content-class="video-call-media-settings-overlay"
    location="top end"
    offset="8"
    @after-enter="focusCloseElement"
    @after-leave="restoreActivatorFocus"
  >
    <template #activator="{ props: activatorProps }">
      <button
        v-bind="activatorProps"
        ref="activatorElement"
        type="button"
        class="media-settings__trigger"
        :class="{ 'media-settings__trigger--overlay': overlay }"
        aria-label="Abrir configurações de áudio e vídeo"
        aria-haspopup="dialog"
        title="Configurações de áudio e vídeo"
        data-testid="video-call-media-settings-trigger"
      >
        <i class="mdi mdi-cog-outline" aria-hidden="true" />
      </button>
    </template>

    <section
      class="media-settings__panel"
      role="dialog"
      aria-label="Configurações de áudio e vídeo"
      data-testid="video-call-media-settings"
    >
      <header>
        <div>
          <span>Chamada</span>
          <h3>Configurações</h3>
        </div>
        <button
          ref="closeElement"
          type="button"
          aria-label="Fechar configurações"
          @click="menuOpen = false"
        >
          <i class="mdi mdi-close" aria-hidden="true" />
        </button>
      </header>

      <div class="media-settings__devices">
        <label>
          <span><i class="mdi mdi-video-outline" /> Câmera</span>
          <select
            :value="selectedCameraDeviceId"
            :disabled="deviceSelectionDisabled || !cameraDevices.length"
            aria-label="Selecionar câmera"
            @change="selectDevice('videoinput', $event)"
          >
            <option v-if="!cameraDevices.length" value="">
              Nenhuma câmera disponível
            </option>
            <option v-else-if="!selectedCameraDeviceId" value="" disabled>
              Selecione uma câmera
            </option>
            <option
              v-for="(device, index) in cameraDevices"
              :key="`${device.kind}-${device.deviceId || index}`"
              :value="device.deviceId"
            >
              {{ getDeviceLabel(device, index, "Câmera") }}
            </option>
          </select>
        </label>

        <label>
          <span><i class="mdi mdi-microphone-outline" /> Microfone</span>
          <select
            :value="selectedMicrophoneDeviceId"
            :disabled="deviceSelectionDisabled || !microphoneDevices.length"
            aria-label="Selecionar microfone"
            @change="selectDevice('audioinput', $event)"
          >
            <option v-if="!microphoneDevices.length" value="">
              Nenhum microfone disponível
            </option>
            <option v-else-if="!selectedMicrophoneDeviceId" value="" disabled>
              Selecione um microfone
            </option>
            <option
              v-for="(device, index) in microphoneDevices"
              :key="`${device.kind}-${device.deviceId || index}`"
              :value="device.deviceId"
            >
              {{ getDeviceLabel(device, index, "Microfone") }}
            </option>
          </select>
        </label>

        <p
          v-if="deviceStatusMessage"
          class="media-settings__status"
          :class="{
            'media-settings__status--error': mediaDevicesStatus === 'error',
          }"
          :role="mediaDevicesStatus === 'error' ? 'alert' : 'status'"
        >
          {{ deviceStatusMessage }}
        </p>
      </div>
    </section>
  </v-menu>
</template>

<style scoped lang="scss">
.media-settings__trigger {
  display: grid;
  place-items: center;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 1px solid #d4dbe7;
  border-radius: 12px;
  background: #eef1f7;
  color: #33425e;
  cursor: pointer;

  i {
    font-size: 1.12rem;
  }

  &:focus-visible {
    outline: 3px solid rgba(49, 86, 255, 0.28);
    outline-offset: 2px;
  }
}

.media-settings__trigger--overlay {
  border-color: rgba(255, 255, 255, 0.24);
  background: rgba(20, 30, 52, 0.84);
  color: #fff;
  backdrop-filter: blur(8px);
}

.media-settings__panel {
  width: min(336px, calc(100vw - 32px));
  max-height: calc(100dvh - 32px);
  padding: 14px;
  overflow-y: auto;
  overscroll-behavior: contain;
  border: 1px solid #dbe1ef;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 18px 48px rgba(17, 30, 55, 0.24);
  color: #26334d;

  > header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 11px;
    border-bottom: 1px solid #e7ebf2;

    span {
      color: #6f7b91;
      font-size: 0.64rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
    }

    h3 {
      margin: 2px 0 0;
      color: #17233c;
      font-size: 0.96rem;
    }

    button {
      display: grid;
      place-items: center;
      flex: 0 0 44px;
      width: 44px;
      height: 44px;
      padding: 0;
      border: 0;
      border-radius: 10px;
      background: #f0f3f8;
      color: #4f5d75;
      cursor: pointer;
    }
  }
}

.media-settings__devices {
  display: grid;
  gap: 10px;
  padding: 12px 0;

  label {
    display: grid;
    gap: 5px;

    > span {
      color: #44516a;
      font-size: 0.72rem;
      font-weight: 700;
    }
  }

  select {
    width: 100%;
    min-height: 44px;
    padding: 8px 34px 8px 10px;
    border: 1px solid #c7d0df;
    border-radius: 10px;
    outline: none;
    background: #fff;
    color: #26334d;
    font: inherit;
    font-size: 0.78rem;

    &:focus-visible {
      border-color: #3156ff;
      box-shadow: 0 0 0 3px rgba(49, 86, 255, 0.12);
    }

    &:disabled {
      cursor: not-allowed;
      background: #f3f5f9;
      color: #7b8699;
    }
  }
}

.media-settings__status {
  margin: 0;
  color: #667188;
  font-size: 0.7rem;
  line-height: 1.4;
}

.media-settings__status--error {
  color: #a51f34;
}
</style>
